import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDataTable, HpiStatsBar } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface TimeItem {
  id: number
  type: "time"
  date: string | null
  case_name: string | null
  service_type: string | null
  hours: number | null
  rate: number | null
  amount: number
}

interface MileageItem {
  id: number
  type: "mileage"
  date: string | null
  case_name: string | null
  miles: number
  rate: number | null
  amount: number
}

interface ExpenseItem {
  id: number
  type: "expense"
  date: string | null
  case_name: string | null
  expense_type: string | null
  description: string | null
  amount: number
}

interface Contractor {
  id: number
  name: string
  time_items: TimeItem[]
  time_total: number
  mileage_items: MileageItem[]
  mileage_total: number
  expense_items: ExpenseItem[]
  expense_total: number
}

interface Props {
  contractors: Contractor[]
  can_mark_paid: boolean
  totals: {
    time_total: number
    mileage_total: number
    expense_total: number
    count: number
  }
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

/* ── Columns ───────────────────────────────────────────── */

const timeColumns: HpiColumn<TimeItem>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => <div className="text-sm">{formatDate(row.date)}</div>,
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Service",
    render: (row) => <div className="text-sm">{row.service_type ?? "-"}</div>,
  },
  {
    header: "Hours",
    width: "max-w-[80px]",
    render: (row) => (
      <div className="text-right text-sm">{row.hours?.toFixed(2) ?? "0"}</div>
    ),
  },
  {
    header: "Amount",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-right text-sm font-medium">
        {formatCurrency(row.amount)}
      </div>
    ),
  },
]

const mileageColumns: HpiColumn<MileageItem>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => <div className="text-sm">{formatDate(row.date)}</div>,
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Miles",
    width: "max-w-[80px]",
    render: (row) => <div className="text-right text-sm">{row.miles}</div>,
  },
  {
    header: "Amount",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-right text-sm font-medium">
        {formatCurrency(row.amount)}
      </div>
    ),
  },
]

const expenseColumns: HpiColumn<ExpenseItem>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => <div className="text-sm">{formatDate(row.date)}</div>,
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Type",
    width: "max-w-[120px]",
    render: (row) => <div className="text-sm">{row.expense_type ?? "-"}</div>,
  },
  {
    header: "Amount",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-right text-sm font-medium">
        {formatCurrency(row.amount)}
      </div>
    ),
  },
]

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Accounting", href: "/accounting/expenses" },
  { title: "Subcontractors", href: "/accounting/subcontractors" },
]

export default function Subcontractors({
  contractors,
  can_mark_paid,
  totals,
}: Props) {
  const [selectedTime, setSelectedTime] = useState<Set<number>>(new Set())
  const [selectedMileage, setSelectedMileage] = useState<Set<number>>(new Set())
  const [selectedExpenses, setSelectedExpenses] = useState<Set<number>>(
    new Set(),
  )
  const [reference, setReference] = useState("")
  const [expandedContractor, setExpandedContractor] = useState<number | null>(
    null,
  )

  const selectAllForContractor = (c: Contractor) => {
    setSelectedTime((prev) => {
      const next = new Set(prev)
      c.time_items.forEach((i) => next.add(i.id))
      return next
    })
    setSelectedMileage((prev) => {
      const next = new Set(prev)
      c.mileage_items.forEach((i) => next.add(i.id))
      return next
    })
    setSelectedExpenses((prev) => {
      const next = new Set(prev)
      c.expense_items.forEach((i) => next.add(i.id))
      return next
    })
  }

  const handleMarkPaid = () => {
    const totalSelected =
      selectedTime.size + selectedMileage.size + selectedExpenses.size
    if (totalSelected === 0) return alert("Please select items to mark as paid")

    router.post("/accounting/subcontractors/mark_paid", {
      reference: reference.trim(),
      time_ids: Array.from(selectedTime),
      mileage_ids: Array.from(selectedMileage),
      expense_ids: Array.from(selectedExpenses),
    })
  }

  const totalSelected =
    selectedTime.size + selectedMileage.size + selectedExpenses.size

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Subcontractors" />

      <div className="p-6">
        <HpiStatsBar
          stats={[
            {
              label: "Unpaid",
              subLabel: "Time",
              value: formatCurrency(totals.time_total),
            },
            {
              label: "Unpaid",
              subLabel: "Mileage",
              value: formatCurrency(totals.mileage_total),
            },
            {
              label: "Unpaid",
              subLabel: "Expenses",
              value: formatCurrency(totals.expense_total),
            },
            { label: "Total", subLabel: "Items", value: totals.count },
          ]}
          className="mb-6"
        />

        {contractors.map((c) => (
          <div
            key={c.id}
            className="border-hpi-light-gray mb-6 rounded-lg border bg-white"
          >
            <button
              className="flex w-full cursor-pointer items-center justify-between p-4 text-left hover:bg-gray-50"
              onClick={() =>
                setExpandedContractor(expandedContractor === c.id ? null : c.id)
              }
            >
              <div>
                <span className="text-hpi-dark-gray font-semibold">
                  {c.name}
                </span>
                <span className="text-hpi-med-blue ml-4 text-sm">
                  {c.time_items.length +
                    c.mileage_items.length +
                    c.expense_items.length}{" "}
                  items
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">
                  {formatCurrency(
                    c.time_total + c.mileage_total + c.expense_total,
                  )}
                </span>
                {can_mark_paid && (
                  <button
                    className="bg-hpi-active-blue hover:bg-hpi-blue-dark cursor-pointer rounded px-3 py-1 text-xs text-white"
                    onClick={(e) => {
                      e.stopPropagation()
                      selectAllForContractor(c)
                    }}
                  >
                    Select All
                  </button>
                )}
              </div>
            </button>

            {expandedContractor === c.id && (
              <div className="border-hpi-light-gray space-y-4 border-t p-4">
                {c.time_items.length > 0 && (
                  <div>
                    <h4 className="text-hpi-dark-gray mb-2 text-sm font-semibold">
                      Unpaid Time
                    </h4>
                    <HpiDataTable<TimeItem>
                      title="Time"
                      columns={timeColumns}
                      data={c.time_items}
                      keyFn={(row) => row.id}
                      selectable={can_mark_paid}
                      selectedKeys={selectedTime}
                      onSelect={(key) =>
                        setSelectedTime((prev) => {
                          const next = new Set(prev)
                          const k = key as number
                          if (next.has(k)) next.delete(k)
                          else next.add(k)
                          return next
                        })
                      }
                    />
                    <div className="mt-1 text-right text-sm font-medium">
                      Total: {formatCurrency(c.time_total)}
                    </div>
                  </div>
                )}
                {c.mileage_items.length > 0 && (
                  <div>
                    <h4 className="text-hpi-dark-gray mb-2 text-sm font-semibold">
                      Unpaid Mileage
                    </h4>
                    <HpiDataTable<MileageItem>
                      title="Mileage"
                      columns={mileageColumns}
                      data={c.mileage_items}
                      keyFn={(row) => row.id}
                      selectable={can_mark_paid}
                      selectedKeys={selectedMileage}
                      onSelect={(key) =>
                        setSelectedMileage((prev) => {
                          const next = new Set(prev)
                          const k = key as number
                          if (next.has(k)) next.delete(k)
                          else next.add(k)
                          return next
                        })
                      }
                    />
                    <div className="mt-1 text-right text-sm font-medium">
                      Total: {formatCurrency(c.mileage_total)}
                    </div>
                  </div>
                )}
                {c.expense_items.length > 0 && (
                  <div>
                    <h4 className="text-hpi-dark-gray mb-2 text-sm font-semibold">
                      Unpaid Expenses
                    </h4>
                    <HpiDataTable<ExpenseItem>
                      title="Expenses"
                      columns={expenseColumns}
                      data={c.expense_items}
                      keyFn={(row) => row.id}
                      selectable={can_mark_paid}
                      selectedKeys={selectedExpenses}
                      onSelect={(key) =>
                        setSelectedExpenses((prev) => {
                          const next = new Set(prev)
                          const k = key as number
                          if (next.has(k)) next.delete(k)
                          else next.add(k)
                          return next
                        })
                      }
                    />
                    <div className="mt-1 text-right text-sm font-medium">
                      Total: {formatCurrency(c.expense_total)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {contractors.length === 0 && (
          <div className="border-hpi-light-gray text-hpi-med-blue rounded-lg border bg-white p-8 text-center">
            No unpaid subcontractor items found.
          </div>
        )}

        {contractors.length > 0 && can_mark_paid && (
          <div className="border-hpi-light-gray mt-6 flex items-center gap-4 rounded-lg border bg-white p-4">
            <label className="text-hpi-dark-gray text-sm font-medium">
              Ref #:
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              maxLength={50}
              className="border-hpi-light-gray focus:border-hpi-active-blue rounded border px-3 py-2 text-sm focus:outline-none"
              placeholder="Optional"
            />
            <button
              onClick={handleMarkPaid}
              className="bg-hpi-dark-green cursor-pointer rounded px-4 py-2 text-sm font-medium text-white hover:opacity-90"
            >
              Mark Paid ({totalSelected} items)
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
